import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { LANGUAGES } from 'projects/core/src/lib/constants';
import { MenuItem, SelectItem } from 'primeng/api';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { Component, Input, Output, EventEmitter, OnChanges, OnInit, OnDestroy } from '@angular/core';
import { EXERCISETYPES, ExerciseModel, ExerciseType } from 'projects/core/src/lib/models/exercise.model';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-exercise-basedata',
  templateUrl: './exercise-basedata.component.html',
  styleUrls: ['./exercise-basedata.component.scss']
})
export class ExerciseBasedataComponent extends BaseForm implements OnInit, OnChanges, OnDestroy {

  @Input() exercise: ExerciseModel = null;
  @Output() setEdit = new EventEmitter<string>();

  public items: Array<MenuItem> = [];
  public languages: Array<SelectItem> = [];
  public types: Array<SelectItem> = [];
  public form: UntypedFormGroup = this.fb.group({
    for_single: new UntypedFormControl(''),
    for_group: new UntypedFormControl(''),
    name: new UntypedFormControl('', [Validators.required]),
    title: new UntypedFormControl('', [Validators.required]),
    language: new UntypedFormControl('', [Validators.required]),
    description: new UntypedFormControl(''),
    group_settings: this.fb.group({
      type: new UntypedFormControl(''),
      min_observers_per_candidate: new UntypedFormControl(''),
      min_candidates_per_team: new UntypedFormControl(''),
      overlapping: new UntypedFormControl(''),
    }),
    // stack_offset: new FormControl('',[Validators.min(MIN_EXERCISE_STACK), Validators.max(MAX_EXERCISE_STACK), CustomValidators.stackOffset()]),
    stack_offset: new UntypedFormControl(''),
  });
  public subscriptions: Array<Subscription> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private router: Router,
    private auth: AuthStateService,
    private confirmationService: ConfirmationService,
    private transloco: TranslocoService,
    private helper: HelperService
  ) {
    super();
  }

  ngOnInit(): void {
    this.languages = FormHelper.getSelectList(LANGUAGES, null, this.transloco);
    this.types = FormHelper.getSelectList(EXERCISETYPES, null, this.transloco);
    this.subscriptions.push(this.form.get('for_group').valueChanges.subscribe(() => {
      this.setFormSettings();
    }));
    this.subscriptions.push(this.form.get('group_settings.type').valueChanges.subscribe(() => {
      this.setTeamSettings();
    }));
    this.ui.setFocus();
  }

  ngOnChanges(): void {
    this.buildMenu();
  }

  ngOnDestroy(): void {
    this.helper.unsetSubscriptions(this.subscriptions);
  }

  public onEdit(): void {
    if (this.auth.hasRight('admin.exercise.edit')) {
      this.startEdit();
    }
  }

  public onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      this.api.saveModel('exercises', this.exercise.id, this.getPayload()).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.form.value.name }), this.transloco.translate('common.saved'));
          this.exercise.deserialize(result.data);
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  public startEdit(): void {
    if (this.exercise !== null) {
      this.form.patchValue({
        for_single: this.exercise.for_single,
        for_group: this.exercise.for_group,
        name: this.exercise.name,
        title: this.exercise.title,
        language: this.exercise.language,
        description: this.exercise.description,
        group_settings: this.exercise.group_settings,
      });
    }
    this.inEdit = true;
    this.setEdit.emit('basedata');
    this.ui.setFormFocus();
  }

  public stopEdit(): void {
    this.inEdit = false;
    this.setEdit.emit(null);
    this.ui.setFocus();
  }

  public onCopyExercise(): void {
    if (this.auth.hasRight('admin.exercise.create')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('exercise_basedata.really_copy', { value: this.exercise.name }),
        header: this.transloco.translate('exercise_basedata.exercise_copied'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.copyExercise(this.exercise.id).subscribe({
            next: (result) => {
              this.ui.toastSuccess(this.transloco.translate('exercise_basedata.copied', { value: this.exercise.name }), this.transloco.translate('common.copied'));
              this.router.navigate(['/exercises', result.data.id]);
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  public onDeleteExercise(): void {
    if (this.auth.hasRight('admin.exercise.delete')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: this.exercise.name }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deleteModel('exercises', this.exercise.id).subscribe({
            next: () => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: this.exercise.name }), this.transloco.translate('common.deleted'));
              this.router.navigate(['/exercises']);
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  public onDeactivateExercise(): void {
    if (this.auth.hasRight('admin.exercise.delete')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('common.messages.really_deactivate', { value: this.exercise.name }),
        header: this.transloco.translate('common.really_deactivate'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deactivateExercise(this.exercise.id).subscribe({
            next: (result: any) => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deactivated', { value: this.exercise.name }), this.transloco.translate('common.deactivated'));
              this.exercise.deserialize(result.data);
              this.buildMenu();
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  public onActivateExercise(): void {
    if (this.auth.hasRight('admin.exercise.delete')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('common.messages.really_activate', { value: this.exercise.name }),
        header: this.transloco.translate('common.really_activate'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.activateExercise(this.exercise.id).subscribe({
            next: (result: any) => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.activated', { value: this.exercise.name }), this.transloco.translate('common.activated'));
              this.exercise.deserialize(result.data);
              this.buildMenu();
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  protected buildMenu(): void {
    this.items = [];
    if (this.exercise !== null) {
      if (this.auth.hasRight('admin.exercise.create')) {
        this.items.push({
          label: this.transloco.translate('common.labels.copy'),
          icon: 'pi pi-copy',
          command: () => {
            this.onCopyExercise();
          }
        });
      }
      if (this.auth.hasRight('admin.exercise.delete')) {
        if (this.exercise.status !== 'inactive') {
          this.items.push({
            label: this.transloco.translate('common.labels.deactivate'),
            icon: 'pi pi-eye-slash',
            command: () => {
              this.onDeactivateExercise();
            }
          });
        }
        if (this.exercise.status === 'inactive') {
          this.items.push({
            label: this.transloco.translate('common.labels.activate'),
            icon: 'pi pi-eye',
            command: () => {
              this.onActivateExercise();
            }
          });
        }
        this.items.push({
          label: this.transloco.translate('common.labels.delete'),
          icon: 'pi pi-trash',
          styleClass: 'danger-button',
          disabled: (!this.exercise.deletable),
          command: () => {
            this.onDeleteExercise();
          }
        });
      }
    }
  }

  public hasGroupSettings(): boolean {
    if (this.inEdit === true) {
      return this.form.get('for_group').value === true;
    }
    return this.exercise.forGroup() === true;
  }

  public isTeam(): boolean {
    if (this.inEdit === true) {
      return this.form.get('group_settings.type').value === ExerciseType.Team;
    }
    return this.exercise.isTeam() === true;
  }

  private getPayload(): any {
    return {
      for_single: this.form.get('for_single').value,
      for_group: this.form.get('for_group').value,
      name: this.form.get('name').value,
      title: this.form.get('title').value,
      language: this.form.get('language').value,
      description: this.form.get('description').value,
      group_settings: (this.form.get('for_group').value === true) ? this.form.get('group_settings').value : null,
    };
  }

  private setFormSettings(): void {
    if (this.form.get('for_group').value === true) {
      this.form.get('group_settings.type').setValidators([Validators.required]);
      this.form.get('group_settings.min_observers_per_candidate').setValidators([Validators.required, Validators.min(1)]);
    } else {
      this.form.get('group_settings.type').clearValidators();
      this.form.get('group_settings.min_observers_per_candidate').clearValidators();
    }
    this.form.get('group_settings.type').updateValueAndValidity();
    this.form.get('group_settings.min_observers_per_candidate').updateValueAndValidity();
  }

  private setTeamSettings(): void {
    if (this.form.get('group_settings.type').value === ExerciseType.Team) {
      this.form.get('group_settings.min_candidates_per_team').setValidators([Validators.required, Validators.min(1)]);
    } else {
      this.form.get('group_settings.min_candidates_per_team').clearValidators();
    }
    this.form.get('group_settings.min_candidates_per_team').updateValueAndValidity();
  }

}
